import { useCallback, useEffect, useRef } from 'react'
import { TableProps } from 'src/antd'
import useLocalStorageState from 'use-local-storage-state'
import { create } from 'zustand'

import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect'

import { DEFAULT_PAGE_SIZE } from './pagination.config'
import { Init } from './useTableState'

const sortDirectionsMap: Record<string, string> = {
  ascend: 'asc',
  descend: 'desc',
}

type PersistentState = {
  pageSize: number
  orderBy: string
  order: string
}

const defaultInit = (init?: Init): PersistentState => ({
  order: init?.order || '',
  orderBy: init?.orderBy || '',
  pageSize: init?.pageSize || DEFAULT_PAGE_SIZE,
})

type PageStore = {
  pageData: Record<string, number>
  setPage: (key: string, page: number) => void
}

/**
 * The current page of every table, for the life of the app (IPFE-12365): a
 * list that is left on page 3 for a detail page comes back on page 3.
 *
 * Keyed by the table's `key`, plus its `pageScope` when it has one — see
 * `useTablePaginationAndSorterAPIHelper`.
 */
const usePageStore = create<PageStore>((set) => ({
  pageData: {},
  setPage: (key, page) => {
    set((state) => ({
      pageData: {
        ...state.pageData,
        [key]: page,
      },
    }))
  },
}))

type AdditionalArgs = {
  /** Deep-compared; any change (search, filters) sends the table to page 1. */
  resetPageDep?: unknown
  /**
   * What the table is showing, for a table that lives on a detail page — the
   * route id for a route's stops, the property id for a property's keys.
   *
   * The remembered PAGE is kept per scope, so page 2 picked on a route with
   * 300 stops is not reused on the next route with 20 (which would be queried
   * at offset 100 and come back empty, PRODUCT-418). The page SIZE and the
   * sort stay shared across scopes: they are the user's preference for this
   * kind of table, not a property of one entity. Leave it out for a table
   * that always shows the same list.
   *
   * Pass the id as soon as the table has the argument at all, resolved or
   * not: a scope that is PRESENT but still `undefined` / '' gets a bucket of
   * its own (`key:`), never the shared unscoped one. `useParams()` ids are
   * `string | undefined`, and a table that read the shared page for one
   * render would bring PRODUCT-418 back — and, through
   * `useClampPageToTotal`, write into every unscoped table's memory.
   */
  pageScope?: string
}

export const useTablePaginationAndSorterAPIHelper = <RecordType,>(
  key: string,
  init?: Init,
  additionalArgs?: AdditionalArgs
) => {
  const isFirstRenderRef = useRef(true)
  const { pageData, setPage: setStoredPage } = usePageStore()
  // Presence, not truthiness: only a caller that never mentions `pageScope`
  // shares the bare `key` with today's 190+ unscoped tables.
  const pageKey =
    additionalArgs && 'pageScope' in additionalArgs
      ? `${key}:${additionalArgs.pageScope ?? ''}`
      : key
  const page = pageData[pageKey] ?? (init?.page || 1)

  const [{ order, orderBy, pageSize }, setState] =
    useLocalStorageState<PersistentState>(`PSort-${key}`, {
      defaultValue: defaultInit(init),
    })

  const onTableChangeHandler: TableProps<RecordType>['onChange'] = (
    pagination,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    _,
    sorter
  ) => {
    if (!Array.isArray(sorter)) {
      setState((p) => ({
        ...p,
        orderBy: `${sorter.field || ''}`,
        order: sorter.order || ``,
      }))
      if (!sorter.order) {
        // the reset case.

        setState((p) => ({
          ...p,
          orderBy: ``,
        }))
      }
    }
    setState((p) => ({
      ...p,
      pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
    }))
    setStoredPage(pageKey, pagination.current || 1)
  }

  const initOrder = init?.order || ''
  const initOrderBy = init?.orderBy || ''

  /**
   * Drop the PERSISTED sort and fall back to the table's declared default.
   *
   * The sort lives in localStorage, so a sort the backend cannot serve does
   * not fail once — it fails on every load, for good, with no way out short
   * of devtools. Callers whose query errors under a known-unservable sort
   * (see `SORT_KEYS_PENDING_BACKEND` in the tickets table) use this to make
   * that failure recoverable.
   */
  const resetSort = useCallback(() => {
    setState((p) =>
      p.order === initOrder && p.orderBy === initOrderBy
        ? p
        : { ...p, order: initOrder, orderBy: initOrderBy }
    )
  }, [setState, initOrder, initOrderBy])

  // A scope change is deliberately NOT a reset: the new scope reads its own
  // remembered page (or 1), and the old one keeps its page for when the user
  // comes back to it.
  useDeepCompareEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }

    setStoredPage(pageKey, init?.page || 1)
  }, [key, additionalArgs?.resetPageDep, init?.page, order, orderBy])

  /** Programmatic page jump (e.g. deep-linking to a specific row). */
  const setPage = useCallback(
    (nextPage: number) => setStoredPage(pageKey, nextPage),
    [setStoredPage, pageKey]
  )

  return {
    paginationVars: {
      first: pageSize,
      offset: pageSize * (page - 1),
      order: sortDirectionsMap[order] || undefined,
      orderBy,
    },
    onTableChangeHandler,
    resetSort,
    setPage,
    state: {
      order,
      orderBy,
      page,
      pageSize,
    },
  }
}

type PageApi = Pick<
  ReturnType<typeof useTablePaginationAndSorterAPIHelper>,
  'setPage' | 'state'
>

/**
 * Keep the remembered page inside the list once its total is known.
 *
 * A page can outlive the rows behind it — the list shrank while the user was
 * away, or the memory came from another entity before `pageScope` was wired.
 * The query then asks for an offset past the end and returns nothing, while
 * antd's pager, which clamps what it DISPLAYS to the page count, shows a
 * perfectly plausible "1" with no way to click out. Once the total is known
 * this moves such a page to the last real one (page 1 for an empty list) and
 * the table re-queries with rows. While the total is still unknown, nothing
 * happens.
 *
 * IT EDITS THE MEMORY. The remembered page is moved, not merely displayed
 * differently: the store then says what the user sees, exactly as it does
 * when antd's pager itself pulls `current` back on a page-size change. So a
 * page 5 that a smaller list (or a larger page size picked on another entity —
 * the size is shared per table) turned into page 2 stays page 2 when the
 * list grows back. Deliberate: the query variables are built before the
 * response that carries the total exists, so a display-only clamp would need
 * a one-render-old copy of the total, which is the hazard below made
 * permanent.
 *
 * IT ACTS ON A NEW TOTAL ONLY — never on a page, page-size or scope change by
 * itself. A table that changes `pageScope` in place has, for that render, the
 * NEW scope's remembered page beside whatever total the query is still
 * reporting; clamping the one against the other would wipe the new scope's
 * memory with the old scope's length. Hence: pass the total from the SAME
 * response as the rows (Apollo reports no `data` for new variables until
 * that scope has answered, so the total goes undefined across the switch),
 * and this hook writes only when the number it is handed changes. A total
 * that happens to be identical across a switch is trusted as it stands.
 *
 * The total lives in the query result, so it is only available AFTER the
 * hook above has produced the variables — hence a separate call:
 *
 *   const pagination = useTablePaginationAndSorterAPIHelper(key, init, args)
 *   const { data } = useQuery(QUERY, { variables: pagination.paginationVars })
 *   useClampPageToTotal(pagination, data?.list?.totalCount)
 */
export const useClampPageToTotal = (
  { state: { page, pageSize }, setPage }: PageApi,
  total: number | null | undefined
) => {
  const seenTotal = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    const isNew = seenTotal.current !== total
    seenTotal.current = total
    if (!isNew || typeof total !== 'number') return
    const lastPage = Math.max(1, Math.ceil(total / pageSize))
    if (page > lastPage) setPage(lastPage)
  }, [total, page, pageSize, setPage])
}
